import {StyleSheet} from "aphrodite";

import Pill from "@khanacademy/wonder-blocks-pill";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {HeadingXSmall} from "@khanacademy/wonder-blocks-typography";
import {doNotTranslateYet} from "@khan/i18n";
import {logUnreachableCode} from "@khan/logging";

import type {Coverage} from "./types.ts";

type Props = {
    coverage: Coverage;
};

function CoverageBadge({coverage}: Props) {
    return (
        <Pill
            kind={pillKind(coverage)}
            style={[
                styles.badge,
                // Setting color via style because Wonder Blocks Pill doesn't
                // have a kind with the color that corresponds to "partially"
                // coverage.
                coverage === "partially" && styles.colorPartially,
            ]}
        >
            <HeadingXSmall tag="span">{displayText(coverage)}</HeadingXSmall>
        </Pill>
    );
}

function pillKind(coverage: Coverage) {
    switch (coverage) {
        case "fully":
            return "success";
        case "mostly":
            return "warning";
        case "partially":
            // Wonder Blocks Pill doesn't have a kind with the color
            // that corresponds to "partially" coverage, so we use
            // "neutral" instead and then set the color via style.
            return "neutral";
        case "not":
            return "critical";
        default:
            throw logUnreachableCode("Unknown coverage value", coverage);
    }
}

function displayText(coverage: Coverage): string {
    switch (coverage) {
        case "fully":
            return doNotTranslateYet`Fully covered`;
        case "mostly":
            return doNotTranslateYet`Mostly covered`;
        case "partially":
            return doNotTranslateYet`Partially covered`;
        case "not":
            return doNotTranslateYet`Not covered`;
        default:
            throw logUnreachableCode("Unknown coverage value", coverage);
    }
}

// Match the background opacity of the Wonder Blocks Pill colors.
const BACKGROUND_OPACITY = 0.16;

const styles = StyleSheet.create({
    badge: {
        borderRadius: spacing.small_12,
        padding: spacing.xSmall_8,
    },
    colorPartially: {
        backgroundColor: `rgba(255, 122, 0, ${BACKGROUND_OPACITY})`,
    },
});

export default CoverageBadge;
